/**
 * <return-form> — wires the "Declare a return" and "Track a return" forms.
 *
 *  • Declare (data-mode="declare", the default): POSTs { orderId, email, locale }
 *    to the Atomic ecom middleware (ArmadaApi) and opens the returnUrl it returns.
 *  • Track (data-mode="track"): POSTs { orderId, email } to the ReverseLogix
 *    encoder (manage-return) and opens the track-all portal URL built from the
 *    returned encrypted email.
 *
 * Markup contract (per <form data-return-form>):
 *   data-mode               — "declare" (default) | "track"
 *   data-endpoint           — endpoint for that form (falls back to the element's)
 *   [name="email"]          — email input
 *   [name="order_number"]   — order number input
 *   button[type="submit"]   — submit button (disabled while loading)
 *   [data-return-status]    — status message target (success / error text)
 *   [data-return-result]    — success result target (the returned URL is rendered here)
 */

// Mirrors snippets/icon-arrow-up-right.liquid — appended to the result link so it
// matches the "leaves the site" arrow rendered on the submit buttons.
const EXTERNAL_LINK_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="icon" aria-hidden="true" focusable="false"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 19.5 15-15m0 0H8.25m11.25 0v11.25" /></svg>';

class ReturnForm extends HTMLElement {
  constructor() {
    super();
    this.endpoint = this.dataset.endpoint || '/orders/validate-return';
    // Track flow uses the middleware manage-return endpoint (returns a returnUrl).
    this.findUrl = this.dataset.findUrl || '/orders/manage-return';
    this.strings = window.returnFormStrings || {};
    this.handleSubmit = this.handleSubmit.bind(this);
    this.forms = Array.from(this.querySelectorAll('form[data-return-form]'));
  }

  connectedCallback() {
    this.forms.forEach((form) => form.addEventListener('submit', this.handleSubmit));
  }

  disconnectedCallback() {
    this.forms.forEach((form) => form.removeEventListener('submit', this.handleSubmit));
  }

  async handleSubmit(event) {
    event.preventDefault();
    const form = event.currentTarget;
    if (form.dataset.loading === 'true') return;

    // Each form carries its own mode. Declare → validate-return, Track →
    // manage-return; both are middleware paths returning a ready returnUrl.
    const mode = form.dataset.mode || 'declare';
    const endpoint =
      form.dataset.endpoint || (mode === 'track' ? this.findUrl : this.endpoint);

    const email = this.fieldValue(form, 'email');
    const orderId = this.fieldValue(form, 'order_number');

    this.clearResult(form);
    this.setStatus(form, '');

    if (!this.validate(form, email, orderId)) return;

    // Misconfigured (e.g. track form with no encoder URL) — fail loudly rather
    // than fall through to the wrong backend.
    if (!endpoint) {
      this.setStatus(form, this.strings.error, 'error');
      return;
    }

    this.setLoading(form, true);

    // Both endpoints take { orderId, email, locale } (locale auto-injected).
    const response = await window.ArmadaApi.post(endpoint, { orderId, email });

    this.setLoading(form, false);

    if (!response.ok) {
      this.setStatus(form, this.errorMessage(response, email, orderId), 'error');
      return;
    }

    this.handleSuccess(form, response.data);
  }

  /**
   * Maps the middleware response onto customer-facing copy. The backend
   * distinguishes the two failure cases by status:
   *
   *   • network / timeout / 5xx — technical failure, nothing the customer can
   *     fix: show the generic "try again" copy.
   *   • 409 — the order exists but isn't complete yet, so it can't be returned.
   *   • any other non-2xx (404/400/422…) — no order matched the email/order
   *     number pair.
   *
   * The last two echo back the values the customer entered.
   */
  errorMessage(response, email, orderId) {
    const isInfraError =
      response.error.type === 'network' ||
      response.error.type === 'timeout' ||
      response.status >= 500;

    if (isInfraError) return this.strings.error;

    const message =
      response.status === 409
        ? this.strings.notReturnable || this.strings.error
        : this.strings.notFound || this.strings.error;

    return (message || '').replace('[email]', email).replace('[order_number]', orderId);
  }

  /**
   * Success handler — both endpoints return a ready URL built server-side:
   * validate-return → `returnUrl` (step1), manage-return → `manageReturnUrl`
   * (track-all). Open it.
   */
  handleSuccess(form, data) {
    const url =
      (data && (data.returnUrl || data.manageReturnUrl || data.url || data.redirectUrl || data.link)) || '';
    this.setStatus(form, this.strings.success, 'success');

    if (!url) return;

    // Open the return portal in a new tab automatically. The async API call
    // breaks the click-gesture chain, so a popup blocker may stop this — we
    // still render the link below as a reliable fallback to click.
    window.open(url, '_blank', 'noopener');

    const target = form.querySelector('[data-return-result]');
    if (!target) return;

    const continueLabel =
      form.dataset.mode === 'track'
        ? this.strings.continueTrack || this.strings.continue
        : this.strings.continue;

    const link = document.createElement('a');
    link.href = url;
    link.textContent = continueLabel || url;
    link.className = 'return-form__result-link';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    // Static markup (no user input) — signals the link opens an external site.
    link.insertAdjacentHTML('beforeend', EXTERNAL_LINK_ICON);

    target.replaceChildren(link);
  }

  fieldValue(form, name) {
    const field = form.querySelector(`[name="${name}"]`);
    return field ? field.value.trim() : '';
  }

  /**
   * Inline per-field validation. Renders a message under each invalid field and
   * focuses the first one. Returns true when everything is valid.
   */
  validate(form, email, orderId) {
    let firstInvalid = null;

    let emailError = '';
    if (!email) emailError = this.strings.emailRequired || this.strings.error;
    else if (!this.isValidEmail(email)) emailError = this.strings.emailInvalid || this.strings.error;
    this.setFieldError(form, 'email', emailError);
    if (emailError) firstInvalid = firstInvalid || 'email';

    const orderError = orderId ? '' : this.strings.orderRequired || this.strings.error;
    this.setFieldError(form, 'order_number', orderError);
    if (orderError) firstInvalid = firstInvalid || 'order_number';

    if (firstInvalid) {
      const input = form.querySelector(`[name="${firstInvalid}"]`);
      if (input) input.focus();
      return false;
    }
    return true;
  }

  isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  setFieldError(form, name, message) {
    const target = form.querySelector(`[data-error-for="${name}"]`);
    const input = form.querySelector(`[name="${name}"]`);
    if (target) {
      target.textContent = message || '';
      target.hidden = !message;
    }
    if (input) {
      if (message) input.setAttribute('aria-invalid', 'true');
      else input.removeAttribute('aria-invalid');
    }
  }

  setLoading(form, isLoading) {
    form.dataset.loading = isLoading ? 'true' : 'false';
    const button = form.querySelector('button[type="submit"]');
    if (!button) return;

    button.disabled = isLoading;
    // Swap only the label text so the external-link icon in the button survives.
    const label = button.querySelector('.return-form__button-label') || button;
    if (isLoading) {
      label.dataset.label = label.textContent;
      if (this.strings.loading) label.textContent = this.strings.loading;
    } else if (label.dataset.label) {
      label.textContent = label.dataset.label;
    }
  }

  setStatus(form, message, type) {
    const status = form.querySelector('[data-return-status]');
    if (!status) return;

    status.textContent = message || '';
    status.classList.toggle('return-form__status--error', type === 'error');
    status.classList.toggle('return-form__status--success', type === 'success');

    if (message && type === 'error') status.setAttribute('role', 'alert');
    else status.removeAttribute('role');
  }

  clearResult(form) {
    const target = form.querySelector('[data-return-result]');
    if (target) target.replaceChildren();
  }
}

if (!customElements.get('return-form')) {
  customElements.define('return-form', ReturnForm);
}
